/**
 * Funnel telemetry — fire-and-forget helpers used by both the pre-auth
 * Signup wizard (kind="signup") and the post-auth Onboarding wizard
 * (kind="onboarding"). Backend dispatches by `kind`.
 *
 * Session id is generated on first call and persisted to sessionStorage so
 * every event from the same browser session groups into one funnel entry.
 */
const API = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = 'safer_signup_funnel_sid';

/**
 * Returns (and lazily creates) a per-browser-session id. Uses sessionStorage
 * so a fresh tab starts a new funnel — matches how marketing teams think
 * about "session" in Mixpanel / PostHog style tools.
 */
export function getSignupFunnelSessionId() {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = `sf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    // Private mode / storage disabled — fall back to a per-call id.
    return `sf_${Date.now().toString(36)}`;
  }
}

/**
 * Emit a single funnel event. Non-blocking — failures are swallowed so the
 * wizard UX is never degraded by telemetry latency or outage.
 *
 * @param {string} step    Step key valid for the chosen kind.
 *                         Signup: account_type | tier_select | payment_option |
 *                                 details | credentials | submit | success | error
 *                         Onboarding: organization | industry | team_setup |
 *                                     policies | review | apply | completed | abandoned
 * @param {string} event   'viewed' | 'completed'
 * @param {object} extras  Optional. May include account_type, selected_tier,
 *                         payment_option, error_code, kind.
 */
export function emitSignupFunnelEvent(step, event = 'viewed', extras = {}) {
  if (!API || !step) return;
  const { kind = 'signup', ...rest } = extras;
  const payload = {
    session_id: getSignupFunnelSessionId(),
    step,
    event,
    kind,
    ...rest,
  };
  try {
    fetch(`${API}/api/signup/funnel/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch {
    // no-op
  }
}

/** Convenience wrapper — onboarding kind shortcut. */
export function emitOnboardingFunnelEvent(step, event = 'viewed', extras = {}) {
  emitSignupFunnelEvent(step, event, { ...extras, kind: 'onboarding' });
}

/**
 * Submit a single answer to the inline "what stopped you?" exit survey.
 * Survey answers ride on the same funnel event endpoint with step="exit_survey",
 * error_code=<reason>, and selected_tier=<step the user was on>. Sticky-dismiss
 * via sessionStorage so a survey only fires once per (kind, step) per session.
 *
 * @param {string} reason  'price' | 'trust' | 'confused' | 'other'
 * @param {object} opts    { kind, fromStep }
 */
export function submitExitSurvey(reason, { kind = 'signup', fromStep = '' } = {}) {
  if (!reason) return;
  emitSignupFunnelEvent('exit_survey', 'completed', {
    kind,
    error_code: reason,
    selected_tier: fromStep,  // re-used as step-origin tag (see backend metrics)
  });
}

/** Has this browser session already seen the exit survey for (kind, step)? */
export function hasSeenExitSurvey(kind, step) {
  try {
    return sessionStorage.getItem(`safer_exit_survey_${kind}_${step}`) === '1';
  } catch {
    return false;
  }
}

/** Mark the survey as seen so it doesn't reappear in this session. */
export function markExitSurveySeen(kind, step) {
  try {
    sessionStorage.setItem(`safer_exit_survey_${kind}_${step}`, '1');
  } catch { /* noop */ }
}
